// GET /reports/:id/photos — the per-visit photo array for the facility
// gerencial dashboard's store-detail gallery (DetalleTienda.tsx, .vgal/
// .vlb). Deliberately its own lazy endpoint, not a field on
// facilityDashboardVisit (packages/api-types/src/facility-dashboard.ts):
// the dashboard aggregate is already a heavy multi-array response fetched
// on every page load, and a manager opens 1-2 store details per session —
// inlining photos there would multiply the payload by every asset's
// visit history for content almost never viewed (2026-08-09 backend
// council, scalability-engineer + data-engineer + react-expert
// independently converged on lazy-per-visit).
//
// area_captures.photoUrls (packages/core-engine/src/entities/area-capture.ts)
// is a bare array of URL strings with no per-photo id, caption or
// capture-time. `id` is synthesized as `${areaCaptureId}:${index}`,
// stable within one response but not a persisted identity.
//
// Per-photo verdict (ADR-0137): the authoritative photo ↔ verdict link is
// report_checklist_results.captured_photo_urls (ADR-0088, since
// 2026-08-13). The supervisor picks it per item at capture time, it is
// written in the same transaction as area_captures.photo_urls, and the AI
// cannot touch it — so a URL found there carries that item's
// supervisor_verdict as `verdict` and the item's label as `itemLabel`.
// report_checklist_results.ai_photo_indices is NOT used for this: the
// model's citations are optional by prompt design and can silently point
// at the wrong photo after a re-capture. A photo with no captured_photo_
// urls match (an acta older than ADR-0088, or a URL attached to two
// different items) gets `verdict: null` and renders as «Sin evaluar»,
// never as a fabricated positive.
//
// Groups (ADR-0137 Postscript, carrusel por observación): `groups` holds
// one card per checklist item that has photos — plus one per area
// capture for photos with no item attribution — so the item's verdict,
// label and caption travel ONCE per card instead of repeating on every
// photo. `photos` stays the flat, ordered array (expand-contract: older
// dashboard bundles still read it); a group points into it by
// `photoIds`, and the photos of one group are contiguous there.
//
// The verdict is the ITEM's, never the photo's: per-photo conformance is
// NOT derived in this delivery. ai_photo_indices means "the model found
// this photo relevant", not "this photo shows the defect", so no photo is
// ever labeled conforming (or defective) from it — the "never a
// fabricated positive" rule above holds per group as well.
//
// `observation` on a group is the live lifecycle of the finding THIS
// acta materialized from that item (report_checklist_results.finding_id)
// — never a carry-over from another acta. It lives only on the group,
// never on a photo. The client composes the state label with
// estadoKindFor / estadoLabelsFor (observation-labels.ts); severity tone
// keeps coming from `verdict`.
//
// Per-photo review (ADR-0169) — an ANNOTATION over the photos the
// gallery shows: the AI's proposal (defecto / conforme / no_concluyente)
// and the supervisor's confirmation, photo by photo. It never changes
// `verdict`, the card's severity tone, its order or the observation
// pill: those keep reading the signed item. The wire carries codes only;
// the dashboard owns the copy («Problema confirmado», «Está bien»…). A
// confirmation's author travels as `actorUserId`, resolved by the client
// against its own membership roster (same contract as the observation
// thread, ADR-0085) — core-api has no user names and sends no emails.
// One photo's review state.
export const reportPhotoReview = z.object({
    phase: z.enum(PHOTO_REVIEW_PHASES),
    // The latest AI proposal for this photo of this item. Shown even when
    // unconfirmed; never affects anything but this field.
    proposal: z
        .object({
        verdict: photoVerdictSchema,
        runId: z.string().uuid(),
        model: z.string(),
        promptVersion: z.string(),
    })
        .nullable(),
    // The latest human confirmation. `corrected` = it differs from the
    // proposal the reviewer was shown (computed server-side, frozen).
    confirmed: z
        .object({
        verdict: confirmedPhotoVerdictSchema,
        corrected: z.boolean(),
        at: z.string().datetime(),
        actorUserId: z.string().uuid(),
    })
        .nullable(),
});
// The item's observation was annulled by this layer (every photo
// confirmed «conforme»). Terminal in v1: there is no undo.
export const reportPhotoGroupAnnulment = z.object({
    applied: z.literal(true),
    reasonCode: photoReviewAnnulmentReasonSchema,
    at: z.string().datetime(),
    actorUserId: z.string().uuid(),
});
import { confirmedPhotoVerdictSchema, OBSERVATION_PHASES, PHOTO_REVIEW_PHASES, photoReviewAnnulmentReasonSchema, photoVerdictSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { assetItemConformance, assetVerdict } from "./asset-state.js";
import { assetObservationSchema } from "./assets.js";
export const reportPhoto = z.object({
    id: z.string().min(1),
    url: z.string().url(),
    areaTag: z.string().nullable(),
    caption: z.string().nullable(),
    capturedAt: z.string().datetime().nullable(),
    // @deprecated — superseded by `verdict` (ADR-0137); still populated
    // during the expand-contract window so older dashboard bundles keep
    // rendering. Never defaulted to "conforme" — an unclassified photo is
    // not the same claim as an inspected-and-passing one.
    conformity: z.enum(["conforme", "critica", "moderada", "leve"]).nullable(),
    // The supervisor verdict of the checklist item this photo was attached
    // to (see file header). Null = no attribution («Sin evaluar»).
    verdict: assetVerdict.nullable().default(null),
    // Label of that checklist item; null under the same conditions.
    // Both default to null on parse so a dashboard bundle newer than the
    // core-api it talks to still accepts a response without them.
    itemLabel: z.string().nullable().default(null),
    // The attributed item's conformance. `verdict` is null both for an
    // item marked n/a and for an item not reviewed, and for a photo with
    // no item at all — this tells them apart: "not_applicable" renders
    // «No aplica», "unreviewed" and null render «Sin evaluar». Null = no
    // item attribution (or a core-api older than this field).
    itemConformance: assetItemConformance.nullable().default(null),
    // ADR-0169 — null when per-photo review does not apply: the report
    // type declares no `photoVerdictReview`, the feature flag is off, or
    // the photo is not attributed to exactly one item, or that item has a
    // single photo (|S| = 1 is not under review: the photo IS the
    // evidence — ADR-0169 Postscript 2026-10-23).
    review: reportPhotoReview.nullable().default(null),
});
// Lifecycle overlay of the finding the acta materialized from an item.
// Exactly the inputs estadoKindFor needs (phase, reopened, reopenCount,
// closedAt, closedReason) plus the finding id for a drill-down link —
// not the raw observationStatus (the phase is the one derivation,
// fitness: observation-phase-single-source) and not effectiveSeverity
// (the card's severity tone is the item verdict's). The picked fields
// are optional on assetObservationSchema for ADR-0021 D6; here the
// server always sends them, so they are required (still nullable).
export const reportPhotoGroupObservation = assetObservationSchema
    .pick({ observationClosedAt: true, observationClosedReason: true, reopenCount: true })
    .required()
    .extend({
    findingId: z.string().uuid(),
    // Derived server-side by core-engine's observationPhase(), which
    // reads the closure overlay: a finding closed by conformidad keeps
    // its observationStatus but reads "closed".
    observationPhase: z.enum(OBSERVATION_PHASES),
    reopened: z.boolean(),
});
export const reportPhotoGroup = reportPhoto
    .pick({ areaTag: true, caption: true, verdict: true, itemLabel: true, itemConformance: true })
    .extend({
    // The item's report_checklist_results.id; `unattributed:<areaCaptureId>`
    // for the photos of one area capture with no item attribution. Stable
    // across re-fetches of the same report (React key), not a route param.
    id: z.string().min(1),
    // Catalog item id (inspection_checklist_items.id); null for an
    // unattributed group.
    itemKey: z.string().nullable(),
    // Null when the item materialized no finding in this acta, the
    // recipe has no observation lifecycle, or the group is unattributed.
    observation: reportPhotoGroupObservation.nullable().default(null),
    // `photos[].id` of this group, in display order — at most
    // PHOTOS_PER_ITEM (3) for an item group; unattributed groups are not
    // capped.
    photoIds: z.array(reportPhoto.shape.id),
    // The group's photo count BEFORE the per-item cap («+N fotos»).
    totalPhotos: z.number().int().nonnegative(),
    // ADR-0169 — |S|: the item's captured photo URLs, deduplicated (the
    // review set). 0 for an unattributed group. Defaults below keep a
    // dashboard newer than its core-api parsing.
    photoSetSize: z.number().int().nonnegative().default(0),
    // The viewer's dashboard may offer the per-photo buttons on this
    // card: terminal acta, review enabled, the item materialized a
    // finding and |S| ≥ 2 (a single photo is not reviewed). Independent of the viewer's lane —
    // that is `photoReview.canReview` on the response.
    reviewable: z.boolean().default(false),
    // ADR-0169 Postscript 2026-10-23 — the card is under per-photo review
    // (core-engine itemUnderPhotoReview): review enabled, the item has an
    // observation, |S| ≥ 2 and ≥ 2 photos of S on the card. Only then do
    // its photos carry `review`. Independent of terminal state and lane,
    // unlike `reviewable`. Optional, NOT defaulted: absent means a core-api
    // older than the field, and the dashboard falls back to its own check.
    underPhotoReview: z.boolean().optional(),
    // Server-computed: confirming the one remaining photo as «conforme»
    // would annul the observation (2 ≤ |S| ≤ 3, every other photo of S
    // already confirmed conforme, the pending one is displayed, and the
    // observation has a legal annul edge). The client asks for the
    // explicit acknowledgement BEFORE sending, and sends it along.
    wouldAnnulOnNextConforme: z.boolean().default(false),
    annulment: reportPhotoGroupAnnulment.nullable().default(null),
});
export const reportPhotosResponse = z.object({
    reportId: z.string().uuid(),
    signedAt: z.string().datetime().nullable(),
    // ADR-0098 — the terminal timestamp for a report closed by unilateral
    // attestation. Without this, a consumer can't tell an attestation-
    // closed visit's photos are equally immutable as a signed one's
    // (signedAt stays null for both an in-progress and an attested
    // report). Read via reportTerminalAt()/isReportTerminal() from
    // @bitacorapro/core-engine, never `signedAt` alone.
    closedAt: z.string().datetime().nullable(),
    photos: z.array(reportPhoto),
    // One card per item (see file header), in display order. Empty for
    // report types whose state source isn't "checklist_items". Defaults to
    // [] so a dashboard newer than its core-api still parses the response.
    groups: z.array(reportPhotoGroup).default([]),
    // ADR-0169 — `enabled`: the report type declares photoVerdictReview
    // AND core-api's PHOTO_VERDICT_REVIEW_ENABLED is on. `canReview`: also
    // the viewer's lane may confirm (assigning_party, not read-only).
    photoReview: z
        .object({ enabled: z.boolean(), canReview: z.boolean() })
        .default({ enabled: false, canReview: false }),
});
// POST /reports/:id/photo-reviews — ONE photo per request, by design
// (ADR-0169 compliance: individual confirmation, no bulk, no
// preselection). Deliberately no array field anywhere in this body.
export const photoReviewRequest = z
    .object({
    resultId: z.string().uuid(),
    photoUrl: z.string().url().max(2048),
    verdict: confirmedPhotoVerdictSchema,
    // The proposal run the reviewer was looking at (null = none shown).
    // Compare-and-set: a newer proposal arriving in between → 409
    // CONFIRMATION_STALE, the client refetches and shows it.
    seenProposalRunId: z.string().uuid().nullable(),
    // Explicit acknowledgement that this confirmation annuls the
    // observation (irreversible). Without it the server answers 409
    // ANNULMENT_ACK_REQUIRED with `details.annulmentPreview`.
    acknowledgeAnnulment: z.boolean().default(false),
})
    .strict();
// `details.annulmentPreview` of a 409 ANNULMENT_ACK_REQUIRED.
export const photoReviewAnnulmentPreview = z.object({
    findingId: z.string().uuid(),
    toStatus: z.string(),
    toStatusLabel: z.string(),
    reasonCode: photoReviewAnnulmentReasonSchema,
    note: z.string(),
});
export const photoReviewResponse = z.object({
    review: reportPhotoReview,
    group: reportPhotoGroup.pick({
        id: true,
        observation: true,
        photoSetSize: true,
        reviewable: true,
        underPhotoReview: true,
        wouldAnnulOnNextConforme: true,
        annulment: true,
    }),
    // This very request annulled the observation.
    annulled: z.boolean(),
});
// POST /internal/checklist-results/photo-proposals — ai-svc → core-api.
// Insert-only; re-running the same run is a no-op per (result, url, run).
export const internalPhotoProposalsRequest = z
    .object({
    organizationId: z.string().uuid(),
    reportId: z.string().uuid(),
    runId: z.string().uuid(),
    model: z.string().min(1).max(200),
    promptVersion: z.string().min(1).max(100),
    proposals: z
        .array(z
        .object({
        resultId: z.string().uuid(),
        photoUrl: z.string().url().max(2048),
        verdict: photoVerdictSchema,
        rationale: z.string().max(2000).optional(),
        photoSha256: z
            .string()
            .regex(/^[0-9a-f]{64}$/)
            .nullable()
            .optional(),
    })
        .strict())
        .min(1)
        .max(200),
})
    .strict();
export const internalPhotoProposalsResponse = z.object({
    inserted: z.number().int().nonnegative(),
    // Already present for this run (idempotent re-run).
    duplicates: z.number().int().nonnegative(),
    // Proposals whose URL is not among the result's captured photos.
    skipped: z.array(z.object({
        resultId: z.string().uuid(),
        photoUrl: z.string(),
        reason: z.literal("photo_not_in_result"),
    })),
});
//# sourceMappingURL=report-photos.js.map